/**
 * Integrytas Group - Base App (MOBILE)
 * Page: FDM.MAP — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/FdmDependencyMapPage.tsx. The desktop
 * is a two-pane dependency GRAPH/map; this is a distinct, mobile-native presentation
 * of the SAME data/logic layer — a scannable, searchable card list of dependency
 * nodes with a per-node detail bottom-sheet that shows what each node depends on,
 * what depends on it, and (on demand) a transitive impact / breaking-change analysis.
 *
 * READ-ONLY (§16: never the desktop grid re-hosted). Reads only:
 *   - apiDb.from('fdm_node')                — the node catalogue,
 *   - apiDb.rpc('fdm_node_graph', …)        — one node's direct deps/dependents,
 *   - apiDb.rpc('fdm_impact_for', …)        — its transitive impact set.
 * No mutations, so no routed_writes import (the desktop "Reparse" rebuild stays a
 * desktop admin action). node_type / relationship are CHECK-style enums rendered
 * through i18n labelled constants, never bare stored literals (§17.B4/B11); the
 * node_key/label are stored technical identifiers (no _tl localisation applies).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Network, ArrowUpRight, ArrowDownLeft, AlertTriangle, Radar } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileFilterChips, MobileStatusPill, type MobileLeadTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

interface NodeRow {
  fdm_node_id: number;
  node_type: string;
  node_key: string;
  label: string | null;
  source_table: string | null;
  source_pk: string | null;
}
interface GraphRef {
  node_type: string;
  node_key: string;
  label: string | null;
  relationship: string;
}
interface NodeGraph {
  node: NodeRow | null;
  dependencies: GraphRef[];
  dependents: GraphRef[];
}
interface ImpactRow {
  node_type: string;
  node_key: string;
  label: string | null;
  relationship: string;
  depth: number;
  direct: boolean;
}

// Node-type lead colour — scannable, mobile-friendly grouping of the artifact kinds.
const TYPE_LEAD: Record<string, string> = {
  SYSTEM_PARAMETER: 'm-l-blue',
  TABLE: 'm-l-slate',
  TABLE_COLUMN: 'm-l-slate',
  DOC_TEMPLATE: 'm-l-green',
  ECM_TEMPLATE: 'm-l-green',
  MTA_RULE: 'm-l-amber',
  UPP_DEFINITION: 'm-l-amber',
  PAGE: 'm-l-blue',
};

// Map the internal lead class strings onto the Direction-C primitive lead tones.
const LEAD_TONE_KEY: Record<string, MobileLeadTone> = {
  'm-l-green': 'green', 'm-l-blue': 'blue', 'm-l-amber': 'amber', 'm-l-red': 'red', 'm-l-slate': 'slate',
};

export function MobileFdmDependencyMapPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();

  // node_type is a CHECK-style enum — labelled via i18n, never the raw literal (§17.B11).
  const typeLabel = (ty: string): string => {
    const map: Record<string, string> = {
      SYSTEM_PARAMETER: t('fdm.type.system_parameter', 'System Parameter'),
      TABLE: t('fdm.type.table', 'Table'),
      TABLE_COLUMN: t('fdm.type.column', 'Column'),
      DOC_TEMPLATE: t('fdm.type.report', 'Report'),
      ECM_TEMPLATE: t('fdm.type.comm_template', 'Comm Template'),
      MTA_RULE: t('fdm.type.business_event', 'Business Event'),
      UPP_DEFINITION: t('fdm.type.import_export', 'Import/Export'),
      PAGE: t('fdm.type.page', 'Page'),
    };
    return map[ty] ?? ty;
  };
  // relationship is server-derived edge data — localise via i18n with a humanised fallback.
  const relLabel = (rel: string): string => t(`mobile.fdm.rel.${rel}`, rel.replace(/_/g, ' '));

  const [nodes, setNodes] = useState<NodeRow[]>([]);
  const [filter, setFilter] = useState('');
  const [typeFacet, setTypeFacet] = useState<string>('');
  const [selected, setSelected] = useState<NodeRow | null>(null);
  const [graph, setGraph] = useState<NodeGraph | null>(null);
  const [impact, setImpact] = useState<ImpactRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [impactBusy, setImpactBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('fdm_node')
        .select('fdm_node_id, node_type, node_key, label, source_table, source_pk')
        .order('node_type')
        .order('node_key')
        .limit(1000);
      if (e) throw e;
      setNodes((data ?? []) as NodeRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load the node catalogue once on mount.
  useEffect(() => { void reload(); }, []);

  const types = useMemo(
    () => Array.from(new Set(nodes.map((n) => n.node_type))).sort(),
    [nodes],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return nodes.filter((n) => {
      if (typeFacet && n.node_type !== typeFacet) return false;
      if (!q) return true;
      return (
        n.node_key.toLowerCase().includes(q) ||
        (n.label ?? '').toLowerCase().includes(q)
      );
    });
  }, [nodes, filter, typeFacet]);

  const selectNode = async (n: NodeRow) => {
    setSelected(n);
    setGraph(null);
    setImpact(null);
    setError(null);
    setSheetBusy(true);
    try {
      const { data, error: e } = await apiDb.rpc<NodeGraph>('fdm_node_graph', {
        p_node_type: n.node_type,
        p_node_key: n.node_key,
      });
      if (e) throw e;
      setGraph(data ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSheetBusy(false);
    }
  };

  const runImpact = async () => {
    if (!selected) return;
    setImpactBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<ImpactRow[]>('fdm_impact_for', {
        p_node_type: selected.node_type,
        p_node_key: selected.node_key,
      });
      if (e) throw e;
      setImpact(Array.isArray(data) ? data : []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setImpactBusy(false);
    }
  };

  const pickRef = (r: GraphRef) => {
    const found = nodes.find((n) => n.node_type === r.node_type && n.node_key === r.node_key);
    if (found) void selectNode(found);
  };

  const closeSheet = () => {
    setSelected(null);
    setGraph(null);
    setImpact(null);
  };

  const dependsOn = graph?.dependencies ?? [];
  const dependents = graph?.dependents ?? [];

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.fdm.title', 'Dependency Map')}
        description={t('mobile.fdm.description', 'What each platform artifact depends on, and what would break if it changed.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.fdm.search', 'Search dependency nodes')}
          aria-label={t('mobile.fdm.search', 'Search dependency nodes')}
        />
      </div>

      <MobileFilterChips
        chips={[
          { value: '', label: t('mobile.fdm.all_types', 'All') },
          ...types.map((ty) => ({ value: ty, label: typeLabel(ty) })),
        ]}
        isActive={(v) => typeFacet === v}
        onSelect={(v) => setTypeFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard>
          <div className="m-empty">
            <Inbox /> {t('mobile.fdm.none', 'No dependency nodes match')}
          </div>
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((n) => (
            <MobileCardRow
              key={n.fdm_node_id}
              lead={<Network />}
              leadTone={LEAD_TONE_KEY[TYPE_LEAD[n.node_type] ?? 'm-l-slate']}
              title={n.label && n.label !== n.node_key ? n.label : n.node_key}
              subtitle={n.node_key}
              trailing={<MobileStatusPill tone="info">{typeLabel(n.node_type)}</MobileStatusPill>}
              chevron
              onClick={() => void selectNode(n)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet — dependencies, dependents, on-demand impact */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={closeSheet} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.fdm.detail', 'Node dependencies')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Network size={20} /></span>
              <span>
                <div className="m-nm">{selected.label && selected.label !== selected.node_key ? selected.label : selected.node_key}</div>
                <div className="m-em">{typeLabel(selected.node_type)} · {selected.node_key}</div>
              </span>
            </div>

            <div className="m-kpis">
              <div className="m-kpi">
                <div className="m-kpi-l">{t('mobile.fdm.kpi.depends_on', 'Depends on')}</div>
                <div className="m-kpi-v">{graph ? dependsOn.length.toLocaleString() : '—'}</div>
              </div>
              <div className="m-kpi">
                <div className="m-kpi-l">{t('mobile.fdm.kpi.dependents', 'Dependents')}</div>
                <div className="m-kpi-v">{graph ? dependents.length.toLocaleString() : '—'}</div>
              </div>
              <div className="m-kpi">
                <div className="m-kpi-l">{t('mobile.fdm.kpi.affected', 'Affected')}</div>
                <div className="m-kpi-v">{impact ? impact.length.toLocaleString() : '—'}</div>
              </div>
            </div>

            {/* Depends on (this node uses) */}
            <div className="m-fieldcard">
              <div className="m-field">
                <span className="m-k">{t('mobile.fdm.depends_on', 'Depends on (this node uses)')}</span>
              </div>
              {dependsOn.length === 0 ? (
                <div className="m-field"><span className="m-v">{sheetBusy ? t('common.loading', 'Loading…') : t('mobile.fdm.rel_none', 'None')}</span></div>
              ) : (
                <div className="m-list">
                  {dependsOn.map((r, i) => (
                    <MobileCardRow
                      key={`dep:${r.node_type}:${r.node_key}:${i}`}
                      lead={<ArrowUpRight />}
                      leadTone="slate"
                      title={r.label && r.label !== r.node_key ? r.label : r.node_key}
                      subtitle={`${typeLabel(r.node_type)} · ${relLabel(r.relationship)}`}
                      chevron
                      onClick={() => pickRef(r)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Dependents (would break) */}
            <div className="m-fieldcard">
              <div className="m-field">
                <span className="m-k">{t('mobile.fdm.dependents', 'Dependents (would break)')}</span>
              </div>
              {dependents.length === 0 ? (
                <div className="m-field"><span className="m-v">{sheetBusy ? t('common.loading', 'Loading…') : t('mobile.fdm.rel_none', 'None')}</span></div>
              ) : (
                <div className="m-list">
                  {dependents.map((r, i) => (
                    <MobileCardRow
                      key={`dependent:${r.node_type}:${r.node_key}:${i}`}
                      lead={<ArrowDownLeft />}
                      leadTone="amber"
                      title={r.label && r.label !== r.node_key ? r.label : r.node_key}
                      subtitle={`${typeLabel(r.node_type)} · ${relLabel(r.relationship)}`}
                      chevron
                      onClick={() => pickRef(r)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Impact / breaking-change analysis (on demand) */}
            {impact && (
              <div className="m-fieldcard">
                <div className="m-field">
                  <span className="m-k">{t('mobile.fdm.impact', 'Impact analysis')}</span>
                  <span className="m-v">
                    {impact.length > 0
                      ? <span className="m-pill m-p-no"><AlertTriangle size={12} /> {t('mobile.fdm.breaking', 'Breaking change')}</span>
                      : <span className="m-pill m-p-ok">{t('mobile.fdm.safe', 'Safe to change')}</span>}
                  </span>
                </div>
                {impact.length > 0 && (
                  <div className="m-list">
                    {impact.map((r, i) => (
                      <MobileCardRow
                        key={`impact:${r.node_type}:${r.node_key}:${i}`}
                        lead={<Radar />}
                        leadTone={r.direct ? 'red' : 'slate'}
                        title={r.label && r.label !== r.node_key ? r.label : r.node_key}
                        subtitle={`${typeLabel(r.node_type)} · ${relLabel(r.relationship)}`}
                        trailing={
                          <MobileStatusPill tone={r.direct ? 'no' : 'info'}>
                            {r.direct
                              ? t('mobile.fdm.direct', 'Direct')
                              : t('mobile.fdm.hops', '{{depth}} hops', { depth: r.depth })}
                          </MobileStatusPill>
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="m-actionbar">
              <button
                type="button"
                className="m-btn m-btn-primary"
                disabled={sheetBusy || impactBusy}
                onClick={() => void runImpact()}
              >
                <Radar size={16} />
                {impactBusy ? t('mobile.fdm.analysing', 'Analysing…') : t('mobile.fdm.run_impact', 'Run impact analysis')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" onClick={closeSheet} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
